/**
 * Learning Package Statistics Page
 * Creator dashboard with engagement KPIs, visit trends, item engagement, the
 * visitors-to-completers funnel, the participants table with roster name resolution,
 * and a leaderboard tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CrownOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  IdcardOutlined,
  MergeCellsOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Line, Column } from '@ant-design/charts';
import PagesWrapper from '../../PagesWrapper';
import PackageTimeline from '../../components/PackageTimeline';
import {
  createAnonymousKeyAlias,
  getLearningPackageParticipants,
  getLearningPackageStatistics,
  getPackageParticipantDetails,
  PackageItemTypes,
} from '../../services/learningPackagesService';
import styles from './LearningPackages.module.css';

/**
 * Renders the creator statistics dashboard for one learning package.
 * @returns {JSX.Element} Learning package statistics page
 */
const LearningPackageStatistics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const packageId = parseInt(id, 10);

  const [statistics, setStatistics] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [aliasTarget, setAliasTarget] = useState(null);
  const [aliasSaving, setAliasSaving] = useState(false);
  const [aliasForm] = Form.useForm();

  const [selectedActorKeys, setSelectedActorKeys] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [timelineOrder, setTimelineOrder] = useState('newest');
  const [engagementMetric, setEngagementMetric] = useState('openCount');
  const [hiddenItemKeys, setHiddenItemKeys] = useState(() => new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [stats, participantsResult] = await Promise.all([
        getLearningPackageStatistics(packageId),
        getLearningPackageParticipants(packageId),
      ]);
      setStatistics(stats);
      setParticipants(participantsResult.participants || []);
    } catch (error) {
      message.error(t('learningPackages.statsFetchError', 'Failed to load statistics'));
    } finally {
      setLoading(false);
    }
  }, [packageId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const itemTypeLabel = (itemType) => {
    switch (itemType) {
      case PackageItemTypes.LearningPath:
        return t('learningPackages.typeLearningPath', 'Learning Path');
      case PackageItemTypes.QuizCollection:
        return t('learningPackages.typeQuizCollection', 'Quiz Collection');
      case PackageItemTypes.TrainingProfile:
        return t('learningPackages.typeTrainingProfile', 'Training Profile');
      default:
        return '';
    }
  };

  const itemTypeColor = (itemType) => {
    switch (itemType) {
      case PackageItemTypes.LearningPath:
        return 'blue';
      case PackageItemTypes.QuizCollection:
        return 'purple';
      case PackageItemTypes.TrainingProfile:
        return 'cyan';
      default:
        return 'default';
    }
  };

  const openAliasModal = (participant) => {
    setAliasTarget(participant);
    aliasForm.setFieldsValue({ displayName: '', note: '' });
    setAliasModalOpen(true);
  };

  const openParticipantDetails = async (actorKeys) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetails(null);
    try {
      const result = await getPackageParticipantDetails(packageId, actorKeys, { suppressNotification: true });
      setDetails(result);
    } catch (error) {
      message.error(t('learningPackages.detailsFetchError', 'Failed to load participant details'));
    } finally {
      setDetailsLoading(false);
    }
  };


  const handleSaveAlias = async () => {
    const values = await aliasForm.validateFields();
    setAliasSaving(true);
    try {
      await createAnonymousKeyAlias({
        anonymousVisitorKey: aliasTarget.anonymousVisitorKey,
        displayName: values.displayName,
        note: values.note || undefined,
      }, { successMessage: t('learningPackages.aliasCreated', 'Visitor name saved to your roster') });
      setAliasModalOpen(false);
      loadData();
    } catch (error) {
      message.error(t('learningPackages.aliasSaveError', 'Failed to save visitor name'));
    } finally {
      setAliasSaving(false);
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

  const itemColumns = [
    {
      title: t('learningPackages.colItem', 'Item'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span>
          {name}
          {!record.isPublished && (
            <Tag color="orange" style={{ marginLeft: 8 }}>{t('learningPackages.draft', 'Draft')}</Tag>
          )}
        </span>
      ),
    },
    {
      title: t('learningPackages.colType', 'Type'),
      dataIndex: 'itemType',
      key: 'itemType',
      width: 160,
      render: (itemType) => <Tag color={itemTypeColor(itemType)}>{itemTypeLabel(itemType)}</Tag>,
    },
    {
      title: t('learningPackages.colOpens', 'Opens'),
      dataIndex: 'openCount',
      key: 'openCount',
      width: 100,
      sorter: (a, b) => a.openCount - b.openCount,
    },
    {
      title: t('learningPackages.colUniqueOpeners', 'Unique Openers'),
      dataIndex: 'uniqueOpeners',
      key: 'uniqueOpeners',
      width: 140,
      sorter: (a, b) => a.uniqueOpeners - b.uniqueOpeners,
    },
    {
      title: t('learningPackages.colCompletions', 'Completions'),
      dataIndex: 'completionCount',
      key: 'completionCount',
      width: 120,
      sorter: (a, b) => a.completionCount - b.completionCount,
    },
    {
      title: t('learningPackages.colAvgScore', 'Avg. Score'),
      dataIndex: 'averageScorePercent',
      key: 'averageScorePercent',
      width: 110,
      render: (value) => (value != null ? `${value}%` : '—'),
    },
  ];

  const participantColumns = [
    {
      title: t('learningPackages.colParticipant', 'Participant'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (displayName, record) => (
        <div>
          <div>
            {displayName}
            {record.isAnonymous && !record.hasAlias && (
              <Tooltip title={t('learningPackages.nameVisitor', 'Name this visitor')}>
                <Button
                  type="link"
                  size="small"
                  icon={<UserAddOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    openAliasModal(record);
                  }}
                />
              </Tooltip>
            )}
          </div>
          {record.isAnonymous && record.anonymousVisitorKey && (
            <div className={styles.anonymousKeyCell}>{record.anonymousVisitorKey}</div>
          )}
        </div>
      ),
    },
    {
      title: t('learningPackages.colProgress', 'Progress'),
      dataIndex: 'progressPercent',
      key: 'progressPercent',
      width: 180,
      sorter: (a, b) => a.progressPercent - b.progressPercent,
      render: (value) => <Progress percent={value} size="small" />,
    },
    {
      title: t('learningPackages.colCompleted', 'Completed'),
      key: 'completed',
      width: 120,
      render: (_, record) => `${record.completedItemCount}/${record.totalItemCount}`,
    },
    {
      title: t('learningPackages.colQuizAttempts', 'Quiz Attempts'),
      dataIndex: 'quizAttemptCount',
      key: 'quizAttemptCount',
      width: 130,
      sorter: (a, b) => a.quizAttemptCount - b.quizAttemptCount,
    },
    {
      title: t('learningPackages.colAvgQuizScore', 'Avg. Quiz Score'),
      dataIndex: 'averageQuizScorePercent',
      key: 'averageQuizScorePercent',
      width: 140,
      render: (value) => (value != null ? `${value}%` : '—'),
      sorter: (a, b) => (a.averageQuizScorePercent ?? -1) - (b.averageQuizScorePercent ?? -1),
    },
    {
      title: t('learningPackages.colFirstSeen', 'First Seen'),
      dataIndex: 'firstSeenAt',
      key: 'firstSeenAt',
      width: 170,
      render: formatDate,
    },
    {
      title: t('learningPackages.colLastSeen', 'Last Seen'),
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 170,
      render: formatDate,
      sorter: (a, b) => new Date(a.lastSeenAt) - new Date(b.lastSeenAt),
    },
  ];

  const leaderboardColumns = [
    {
      title: t('learningPackages.colRank', 'Rank'),
      key: 'rank',
      width: 80,
      render: (_, record, index) => {
        const medals = [styles.medalGold, styles.medalSilver, styles.medalBronze];
        return (
          <span className={`${styles.leaderboardRank} ${index < 3 ? medals[index] : ''}`}>
            {index < 3 ? <CrownOutlined /> : null} #{index + 1}
          </span>
        );
      },
    },
    {
      title: t('learningPackages.colParticipant', 'Participant'),
      dataIndex: 'displayName',
      key: 'displayName',
    },
    {
      title: t('learningPackages.colProgress', 'Progress'),
      dataIndex: 'progressPercent',
      key: 'progressPercent',
      width: 200,
      render: (value) => <Progress percent={value} size="small" />,
    },
    {
      title: t('learningPackages.colAvgQuizScore', 'Avg. Quiz Score'),
      dataIndex: 'averageQuizScorePercent',
      key: 'averageQuizScorePercent',
      width: 150,
      render: (value) => (value != null ? `${value}%` : '—'),
    },
  ];

  const trendData = (statistics?.visitsOverTime || []).map(point => ({
    date: new Date(point.date).toLocaleDateString(),
    visits: point.visits,
    uniqueVisitors: point.uniqueVisitors,
  }));

  const itemTypeChartColors = {
    [PackageItemTypes.LearningPath]: '#1890ff',
    [PackageItemTypes.QuizCollection]: '#722ed1',
    [PackageItemTypes.TrainingProfile]: '#13c2c2',
  };

  const itemKeyOf = (item) => `${item.itemType}-${item.itemId}`;

  const toggleItemVisibility = (item) => {
    setHiddenItemKeys((previous) => {
      const next = new Set(previous);
      const key = itemKeyOf(item);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const engagementChartData = (statistics?.itemStats || [])
    .filter(item => !hiddenItemKeys.has(itemKeyOf(item)))
    .map(item => ({
      name: item.name,
      typeLabel: itemTypeLabel(item.itemType),
      value: item[engagementMetric] ?? 0,
    }));

  const overviewTab = (
    <>
      <Row gutter={[16, 16]} className={styles.kpiRow}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('learningPackages.totalVisits', 'Total Visits')}
              value={statistics?.totalVisits ?? 0}
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('learningPackages.uniqueVisitors', 'Unique Visitors')}
              value={statistics?.uniqueVisitors ?? 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('learningPackages.participants', 'Participants')}
              value={statistics?.participantCount ?? 0}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('learningPackages.avgProgress', 'Average Progress')}
              value={statistics?.averageProgressPercent ?? 0}
              suffix="%"
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title={t('learningPackages.funnelTitle', 'Engagement Funnel')} className={styles.chartCard}>
        <div className={styles.funnelRow}>
          <div className={styles.funnelStage}>
            <div className={styles.funnelValue}>{statistics?.funnel?.visitors ?? 0}</div>
            <div className={styles.funnelLabel}>{t('learningPackages.funnelVisitors', 'Visitors')}</div>
          </div>
          <ArrowRightOutlined className={styles.funnelArrow} />
          <div className={styles.funnelStage}>
            <div className={styles.funnelValue}>{statistics?.funnel?.itemOpeners ?? 0}</div>
            <div className={styles.funnelLabel}>{t('learningPackages.funnelItemOpeners', 'Opened an item')}</div>
          </div>
          <ArrowRightOutlined className={styles.funnelArrow} />
          <div className={styles.funnelStage}>
            <div className={styles.funnelValue}>{statistics?.funnel?.completers ?? 0}</div>
            <div className={styles.funnelLabel}>{t('learningPackages.funnelCompleters', 'Completed everything')}</div>
          </div>
        </div>
      </Card>

      {trendData.length > 0 && (
        <Card title={t('learningPackages.visitsOverTime', 'Visits Over Time')} className={styles.chartCard}>
          <Line
            data={trendData}
            xField="date"
            yField="visits"
            height={280}
            smooth
            point={{ size: 3 }}
          />
        </Card>
      )}

      {(statistics?.itemStats || []).length > 0 && (
        <Card
          title={t('learningPackages.itemEngagement', 'Item Engagement')}
          className={styles.chartCard}
          extra={(
            <Segmented
              value={engagementMetric}
              onChange={setEngagementMetric}
              options={[
                { label: t('learningPackages.colOpens', 'Opens'), value: 'openCount' },
                { label: t('learningPackages.colUniqueOpeners', 'Unique Openers'), value: 'uniqueOpeners' },
                { label: t('learningPackages.colCompletions', 'Completions'), value: 'completionCount' },
              ]}
            />
          )}
        >
          <div className={styles.chartItemToggles}>
            {(statistics?.itemStats || []).map(item => {
              const hidden = hiddenItemKeys.has(itemKeyOf(item));
              const typeColor = itemTypeChartColors[item.itemType] || '#1890ff';
              return (
                <Tag.CheckableTag
                  key={itemKeyOf(item)}
                  checked={!hidden}
                  onChange={() => toggleItemVisibility(item)}
                  style={{
                    border: `1px solid ${typeColor}`,
                    color: hidden ? '#94a3b8' : typeColor,
                    background: hidden ? 'transparent' : `${typeColor}1a`,
                  }}
                >
                  {item.name}
                </Tag.CheckableTag>
              );
            })}
          </div>
          {engagementChartData.length > 0 ? (
            <Column
              data={engagementChartData}
              xField="name"
              yField="value"
              colorField="typeLabel"
              height={280}
              scale={{
                color: {
                  domain: [
                    itemTypeLabel(PackageItemTypes.LearningPath),
                    itemTypeLabel(PackageItemTypes.QuizCollection),
                    itemTypeLabel(PackageItemTypes.TrainingProfile),
                  ],
                  range: [
                    itemTypeChartColors[PackageItemTypes.LearningPath],
                    itemTypeChartColors[PackageItemTypes.QuizCollection],
                    itemTypeChartColors[PackageItemTypes.TrainingProfile],
                  ],
                },
              }}
            />
          ) : (
            <Typography.Text type="secondary">
              {t('learningPackages.allItemsHidden', 'All items are hidden — enable an item above to see the chart.')}
            </Typography.Text>
          )}
        </Card>
      )}

      <Card title={t('learningPackages.itemEngagement', 'Item Engagement')}>
        <Table
          rowKey={(record) => `${record.itemType}-${record.itemId}`}
          dataSource={statistics?.itemStats || []}
          columns={itemColumns}
          pagination={false}
          size="small"
        />
      </Card>
    </>
  );

  const participantsTab = (
    <Card>
      <div className={styles.combineBar}>
        <Button
          icon={<MergeCellsOutlined />}
          disabled={selectedActorKeys.length < 2}
          onClick={() => openParticipantDetails(selectedActorKeys)}
        >
          {t('learningPackages.combineSelected', 'Combine selected')}
          {selectedActorKeys.length >= 2 ? ` (${selectedActorKeys.length})` : ''}
        </Button>
        <Typography.Text type="secondary">
          {t('learningPackages.combineHint', 'Click a row for details, or select several entries (e.g. the same person on two devices) and combine them.')}
        </Typography.Text>
      </div>
      <Table
        rowKey="actorKey"
        dataSource={participants}
        columns={participantColumns}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="small"
        rowClassName={() => styles.participantRow}
        rowSelection={{
          selectedRowKeys: selectedActorKeys,
          onChange: setSelectedActorKeys,
        }}
        onRow={(record) => ({
          onClick: () => openParticipantDetails([record.actorKey]),
        })}
      />
    </Card>
  );

  const leaderboardTab = (
    <Card>
      <Table
        rowKey="actorKey"
        dataSource={participants.slice(0, 25)}
        columns={leaderboardColumns}
        pagination={false}
        size="small"
        rowClassName={() => styles.participantRow}
        onRow={(record) => ({
          onClick: () => openParticipantDetails([record.actorKey]),
        })}
      />
    </Card>
  );

  const detailsQuizColumns = [
    {
      title: t('learningPackages.colItem', 'Item'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span>
          {name}
          {record.isPathElementQuiz && (
            <Tooltip title={t('learningPackages.pathElementQuizHint', 'Linked inside a learning path element')}>
              <Tag style={{ marginLeft: 6 }} icon={<ApartmentOutlined />} color="blue">
                {t('learningPackages.typeLearningPath', 'Learning Path')}
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('learningPackages.colQuizAttempts', 'Quiz Attempts'),
      dataIndex: 'attemptCount',
      key: 'attemptCount',
      width: 90,
    },
    {
      title: t('learningPackages.colBest', 'Best'),
      dataIndex: 'bestScorePercent',
      key: 'bestScorePercent',
      width: 80,
      render: (value) => (value != null ? `${value}%` : '—'),
    },
    {
      title: t('learningPackages.colWorst', 'Worst'),
      dataIndex: 'worstScorePercent',
      key: 'worstScorePercent',
      width: 80,
      render: (value) => (value != null ? `${value}%` : '—'),
    },
    {
      title: t('learningPackages.colAvgScore', 'Avg. Score'),
      dataIndex: 'averageScorePercent',
      key: 'averageScorePercent',
      width: 90,
      render: (value) => (value != null ? `${value}%` : '—'),
    },
    {
      title: t('learningPackages.colLastSeen', 'Last Seen'),
      dataIndex: 'lastAttemptAt',
      key: 'lastAttemptAt',
      width: 140,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
  ];

  const participantDrawer = (
    <Drawer
      title={details?.displayName || t('learningPackages.colParticipant', 'Participant')}
      open={detailsOpen}
      onClose={() => setDetailsOpen(false)}
      width={680}
    >
      <Spin spinning={detailsLoading}>
        {details && (
          <>
            <div className={styles.drawerProgressBlock}>
              <Progress type="circle" size={64} percent={Math.round(details.progressPercent)} />
              <div>
                <Typography.Text strong>
                  {t('learningPackages.journeyCompletedOf', {
                    completed: details.completedItemCount,
                    total: details.totalItemCount,
                    defaultValue: '{{completed}} of {{total}} items completed',
                  })}
                </Typography.Text>
                {details.actorKeys?.length > 1 && (
                  <div>
                    <Tag icon={<MergeCellsOutlined />} color="purple">
                      {t('learningPackages.combinedActors', {
                        count: details.actorKeys.length,
                        defaultValue: '{{count}} combined identities',
                      })}
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            <Tabs
              items={[
                {
                  key: 'achievements',
                  label: t('learningPackages.drawerAchievementsTab', 'Achievements'),
                  children: (
                    <>
                      {(details.quizStats || []).length > 0 && (
                        <>
                          <div className={styles.drawerSectionTitle}>
                            <QuestionCircleOutlined /> {t('learningPackages.drawerQuizzesTitle', 'Quiz performance')}
                          </div>
                          <Table
                            rowKey="quizCollectionId"
                            dataSource={details.quizStats}
                            columns={detailsQuizColumns}
                            pagination={false}
                            size="small"
                          />
                        </>
                      )}

                      {(details.profileStats || []).map(profile => (
                        <div key={profile.trainingProfileId}>
                          <div className={styles.drawerSectionTitle}>
                            <ExperimentOutlined /> {profile.name}
                            <Tag color="cyan">
                              {t('learningPackages.questProgress', {
                                completed: profile.completedQuestCount,
                                total: profile.totalQuestCount,
                                defaultValue: 'Quests: {{completed}}/{{total}}',
                              })}
                            </Tag>
                            {profile.openCount > 0 && (
                              <Typography.Text type="secondary">
                                {t('learningPackages.colOpens', 'Opens')}: {profile.openCount}
                              </Typography.Text>
                            )}
                          </div>
                          {(profile.quests || []).map(quest => (
                            <div key={quest.questKey} className={styles.drawerQuestRow}>
                              {quest.timesCompleted > 0
                                ? <TrophyOutlined style={{ color: '#d4a017', fontSize: 16 }} />
                                : <RightOutlined style={{ color: '#d9d9d9', fontSize: 14 }} />}
                              <div className={styles.drawerQuestName}>
                                {quest.title}
                                <div className={styles.drawerQuestMeta}>
                                  {quest.timesCompleted > 0 && (
                                    <span>{t('learningPackages.timesCompleted', { count: quest.timesCompleted, defaultValue: 'Completed {{count}}×' })}</span>
                                  )}
                                  {quest.lastCompletedAt && <span>{new Date(quest.lastCompletedAt).toLocaleString()}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}

                      {(details.quizStats || []).length === 0 && (details.profileStats || []).length === 0 && (
                        <Typography.Text type="secondary">{t('learningPackages.noActivityYet', 'No activity yet')}</Typography.Text>
                      )}
                    </>
                  ),
                },
                {
                  key: 'timeline',
                  label: (
                    <span><RiseOutlined /> {t('learningPackages.drawerTimelineTab', 'Timeline')}</span>
                  ),
                  children: (
                    <>
                      <div className={styles.timelineOrderBar}>
                        <Segmented
                          value={timelineOrder}
                          onChange={setTimelineOrder}
                          options={[
                            { label: t('learningPackages.timelineNewestFirst', 'Newest first'), value: 'newest' },
                            { label: t('learningPackages.timelineOldestFirst', 'Oldest first'), value: 'oldest' },
                          ]}
                        />
                      </div>
                      <PackageTimeline groups={details.timeline} maxGroups={40} maxEventsPerGroup={12} order={timelineOrder} />
                    </>
                  ),
                },
              ]}
            />
          </>
        )}
      </Spin>
    </Drawer>
  );

  return (
    <PagesWrapper>
      <Spin spinning={loading}>
        <div className={styles.statsContainer}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/learning-packages')}>
                {t('common.back', 'Back')}
              </Button>
              <h1>{statistics?.learningPackageName || t('learningPackages.statsTitle', 'Package Statistics')}</h1>
              <Tag color="blue">{t('learningPackages.statistics', 'Statistics')}</Tag>
            </div>
            <Space className={styles.headerActions}>
              <Tooltip title={t('learningPackages.edit', 'Edit')}>
                <Button icon={<EditOutlined />} onClick={() => navigate(`/learning-packages/${packageId}`)}>
                  {t('learningPackages.edit', 'Edit')}
                </Button>
              </Tooltip>
              <Tooltip title={t('learningPackages.explorePackage', 'Explore Package')}>
                <Button icon={<EyeOutlined />} onClick={() => window.open(`/packages/${packageId}`, '_blank')}>
                  {t('learningPackages.explorePackage', 'Explore Package')}
                </Button>
              </Tooltip>
              <Button icon={<IdcardOutlined />} onClick={() => navigate('/learning-packages/roster')}>
                {t('learningPackages.openRoster', 'Visitor name roster')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadData}>
                {t('common.refresh', 'Refresh')}
              </Button>
            </Space>
          </div>

          <Tabs
            items={[
              { key: 'overview', label: t('learningPackages.tabOverview', 'Overview'), children: overviewTab },
              { key: 'participants', label: t('learningPackages.tabParticipants', 'Participants'), children: participantsTab },
              { key: 'leaderboard', label: t('learningPackages.tabLeaderboard', 'Leaderboard'), children: leaderboardTab },
            ]}
          />
        </div>
      </Spin>

      {participantDrawer}

      <Modal
        title={t('learningPackages.nameVisitor', 'Name this visitor')}
        open={aliasModalOpen}
        onCancel={() => setAliasModalOpen(false)}
        onOk={handleSaveAlias}
        confirmLoading={aliasSaving}
      >
        {aliasTarget && (
          <div className={styles.anonymousKeyCell} style={{ marginBottom: 12 }}>
            {aliasTarget.anonymousVisitorKey}
          </div>
        )}
        <Form form={aliasForm} layout="vertical">
          <Form.Item
            name="displayName"
            label={t('learningPackages.aliasNameLabel', 'Display name')}
            rules={[{ required: true, message: t('learningPackages.nameRequired', 'Name is required') }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="note" label={t('learningPackages.aliasNoteLabel', 'Note (optional)')}>
            <Input.TextArea maxLength={500} rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </PagesWrapper>
  );
};

export default LearningPackageStatistics;
